/**
 * Data transformation utilities for admit adjustment data
 * Separated from the main hook for better testability and reusability
 */

/**
 * Creates regime tabs from raw data
 */
export const createRegimeTabs = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) return [];
  
  const uniqueRegimes = [...new Set(rawData.map(item => item.regime))];
  return uniqueRegimes.map(regime => ({
    id: regime,
    label: regime,
  }));
};

/**
 * Extracts and sorts need rank ranges for a specific regime
 */
export const extractNeedRankRanges = (regimeData) => {
  const rangeMap = new Map();
  
  regimeData.forEach(item => {
    const rangeKey = `${item.needRankRangeStart}_${item.needRankRangeEnd}`;
    if (!rangeMap.has(rangeKey)) {
      rangeMap.set(rangeKey, {
        start: parseInt(item.needRankRangeStart),
        end: parseInt(item.needRankRangeEnd),
        rangeKey,
      });
    }
  });

  return Array.from(rangeMap.values())
    .sort((a, b) => a.start - b.start)
    .map(({ start, end, rangeKey }) => ({
      label: `$${start.toLocaleString()} - ${end.toLocaleString()} Need Rank`,
      min: start,
      max: end,
      rangeKey,
    }));
};

/**
 * Transforms a single data item for display
 */
export const transformDataItem = (item) => ({
  rank: item.academicRank,
  admits: item.admits,
  // Store original values for calculations
  totalProbability: item.totalProbability,
  admitAdj: item.admitAdj,
  yieldAdj: item.yieldAdj,
  pyYield: item.pyYield,
  projYield: item.projYield,
  // Display values
  projectedYield: `${(item.projYield * 100).toFixed(2)}%`,
  increase: item.admitAdj === 1 ? "0" : `${((item.admitAdj - 1) * 100).toFixed(1)}`,
  yield: item.yieldAdj === 1 ? "0" : `${((item.yieldAdj - 1) * 100).toFixed(1)}`,
  yieldPY: `${(item.pyYield * 100).toFixed(2)}%`,
});

/**
 * Groups academic data by need rank for a specific regime
 */
export const groupAcademicDataByNeedRank = (regimeData, needRanks) => {
  const academicDataByNeedRank = {};
  
  needRanks.forEach(({ min, rangeKey }) => {
    const [start, end] = rangeKey.split('_');
    const needRankData = regimeData.filter(
      item => item.needRankRangeStart === start && item.needRankRangeEnd === end
    );
    
    academicDataByNeedRank[min] = needRankData.map(transformDataItem);
  });
  
  return academicDataByNeedRank;
};

/**
 * Transforms raw API data into organized structure
 */
export const transformApiData = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return {};
  }

  const dataByRegime = {};
  const uniqueRegimes = [...new Set(rawData.map(item => item.regime))];

  uniqueRegimes.forEach(regime => {
    const regimeData = rawData.filter(item => item.regime === regime);
    const needRanks = extractNeedRankRanges(regimeData);
    const academicDataByNeedRank = groupAcademicDataByNeedRank(regimeData, needRanks);

    dataByRegime[regime] = {
      needRanks,
      academicDataByNeedRank,
    };
  });

  return dataByRegime;
};

/**
 * Initializes user input structure from backend data
 */
export const initializeUserInput = (backendData) => {
  const input = {};
  
  Object.entries(backendData).forEach(([regime, regimeObj]) => {
    input[regime] = { academicDataByNeedRank: {} };
    
    Object.entries(regimeObj.academicDataByNeedRank).forEach(([needRankMin, rows]) => {
      input[regime].academicDataByNeedRank[needRankMin] = rows.map(item => ({
        increase: item.increase, // already percent string from backend transform
        yield: item.yield, // already percent string from backend transform
      }));
    });
  });
  
  return input;
}; 